import { Component, Input } from '@angular/core';
import {
	CustomFormAccessor,
	FormConfig,
} from 'src/shared/form-models/form-cva.component';
import {
	Address,
	Customer,
	JobBillTo,
	JobPreferredBillingChannel,
	Railroad,
} from 'src/shared/api';
import { FormBuilder, NgControl, Validators } from '@angular/forms';

import { BillingDetails } from './billing-details.models';
import { ReplaySubject } from 'rxjs';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { buildArrayFromEnum } from 'src/shared/utilities/utility-functions';

const billingDetailsFormConfig: FormConfig<BillingDetails> = {
	preferredBillingChannel: ['', [Validators.required]],
	billTo: ['', [Validators.required]],
	otherBillingCompany: [''],
	billingEmails: [''],
	billingNotes: [''],
	billingAddress: [''],
};

@Component({
	selector: 'rpshared-billing-details',
	templateUrl: './billing-details.component.html',
})
export class BillingDetailsComponent extends CustomFormAccessor<
	BillingDetails
> {
	private readonly _billTo = new ReplaySubject<JobBillTo>(1);
	private readonly _billVia = new ReplaySubject<JobPreferredBillingChannel>(
		1
	);
	private currentBillTo: JobBillTo | null = null;

	@Input() railroad: Railroad;
	@Input()
	get customer(): Customer {
		return this._customer;
	}
	set customer(value: Customer) {
		this._customer = value;
		this.ensureCustomerBillingAddress();
	}
	private _customer: Customer;

	@Input()
	get editBillingInfo(): boolean {
		return this._editBillingInfo;
	}
	set editBillingInfo(value: boolean) {
		this._editBillingInfo = coerceBooleanProperty(value);
	}
	private _editBillingInfo = false;

	readonly billTo$ = this._billTo.asObservable();
	readonly billVia$ = this._billVia.asObservable();

	readonly billVias = buildArrayFromEnum(JobPreferredBillingChannel);
	readonly billTos = buildArrayFromEnum(JobBillTo);
	constructor(public ngControl: NgControl, public fb: FormBuilder) {
		super(ngControl, fb, billingDetailsFormConfig);
	}

	writeValue(value: BillingDetails): void {
		if (value) {
			this.form.patchValue(value, { emitEvent: false });
			this.setBillTo(value.billTo);
			this.setBillVia(value.preferredBillingChannel);
			this.form.markAsUntouched();
		}
	}

	setBillTo(billTo: JobBillTo) {
		this.currentBillTo = billTo;
		this._billTo.next(billTo);
		if (billTo !== JobBillTo.Customer) {
			this.form.get('billingAddress').setValue(null, { emitEvent: true });
		} else {
			this.ensureCustomerBillingAddress();
		}
	}

	onBillToSelectionChanged(billTo: JobBillTo) {
		this.setBillTo(billTo);
		if (billTo !== JobBillTo.Other) {
			this.form.patchValue({ otherBillingCompany: null });
		}
	}

	setBillVia(billVia: JobPreferredBillingChannel) {
		this._billVia.next(billVia);
	}

	onBillingAddressSelected(address: Address) {
		this.form.get('billingAddress').setValue(address, { emitEvent: true });
	}

	private ensureCustomerBillingAddress() {
		Promise.resolve().then(() => {
			if (!this.form || this.currentBillTo !== JobBillTo.Customer) {
				return;
			}
			const control = this.form.get('billingAddress');
			if (control.value?.id) {
				return;
			}
			const addresses = this.customer?.companyAddresses ?? [];
			const preferred =
				addresses.find((ca) => ca.isDefault) ??
				(addresses.length === 1 ? addresses[0] : null);
			if (preferred?.address) {
				control.setValue(preferred.address, { emitEvent: true });
			}
		});
	}
}
